import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { extractErrorCode, friendlyAuthError } from '@/lib/auth-errors';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { usePublicSettings } from '@/hooks/usePublicSettings';

interface SignUpFormProps {
  /** Called after a successful sign-up so the host modal can close. */
  onSuccess?: () => void;
  /** Called when the user clicks "have an account? sign in" — switches the AuthModal tab. */
  onSwitchToSignIn?: () => void;
}

/**
 * Account creation: email, optional display name, password, and a server-
 * checked invitation (sign-up) secret. No email verification, no password
 * reset. Inline error panel for EMAIL_TAKEN that suggests switching to sign-in.
 */
export function SignUpForm({ onSuccess, onSwitchToSignIn }: SignUpFormProps) {
  const { signUp } = useAuth();
  const { signupEnabled, loaded } = usePublicSettings();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [signupSecret, setSignupSecret] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorCode(null);
    setErrorText(null);
    setSubmitting(true);
    try {
      await signUp(email.trim(), password, signupSecret, displayName);
      onSuccess?.();
    } catch (err) {
      setErrorCode(extractErrorCode(err));
      setErrorText(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loaded && !signupEnabled) {
    return (
      <div role="status" className="rounded-sm border border-ink/20 bg-paper-2 p-4">
        <p className="text-sm font-medium text-ink">Sign-ups are closed for now.</p>
        <p className="mt-1 text-xs text-graphite leading-relaxed">
          The study team has paused new registrations. If you already have an account, use the
          Sign in tab.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-5">
      <div className="grid gap-2">
        <Label htmlFor="signup-email">Email</Label>
        <Input
          id="signup-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          disabled={submitting}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="signup-name">
          Display name <span className="text-graphite text-xs">(optional)</span>
        </Label>
        <Input
          id="signup-name"
          type="text"
          autoComplete="name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="What should AURA call you?"
          disabled={submitting}
          maxLength={60}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="signup-password">Password</Label>
        <Input
          id="signup-password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters, with letter and digit"
          disabled={submitting}
        />
        <p className="font-mono text-[11px] text-graphite leading-relaxed">
          <span className="text-ochre">¶</span>&nbsp;&nbsp;Eight characters
          minimum. Must contain at least one letter and one digit.
        </p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="signup-secret">Invitation key</Label>
        <Input
          id="signup-secret"
          name="signup-secret"
          type="password"
          autoComplete="off"
          required
          value={signupSecret}
          onChange={(e) => setSignupSecret(e.target.value)}
          placeholder="Required to create an account"
          disabled={submitting}
        />
        <p className="font-mono text-[11px] text-graphite leading-relaxed">
          <span className="text-ochre">¶</span>&nbsp;&nbsp;Ask the people running
          this deployment for the key. It is not your password.
        </p>
      </div>

      {errorCode === 'EMAIL_TAKEN' ? (
        <div
          role="alert"
          className="rounded-sm border border-ink/20 bg-paper-2 p-3"
        >
          <p className="text-sm font-medium text-ink">
            That email is already registered.
          </p>
          {onSwitchToSignIn ? (
            <button
              type="button"
              onClick={onSwitchToSignIn}
              className="mt-1 text-xs underline decoration-ink/30 underline-offset-4 hover:decoration-ochre hover:text-ochre"
            >
              Switch to sign in →
            </button>
          ) : (
            <p className="mt-1 text-xs text-graphite">Try the Sign in tab.</p>
          )}
        </div>
      ) : errorText ? (
        <p role="alert" className="text-sm text-vermilion">
          {errorText}
        </p>
      ) : null}

      <Button type="submit" disabled={submitting} className="w-full">
        {submitting ? <LoadingSpinner label="Creating account…" size={16} /> : 'Create account'}
      </Button>
    </form>
  );
}
